import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import { LayoutGrid } from 'lucide-react';

// Shared dark-theme admin UI styles (site palette: #252525 / #b7a78b)
const ui = {
  card: 'bg-[#1d1d1d] rounded-xl border border-[#484540] p-6 md:p-8 shadow-lg',
  title: 'text-xl font-semibold mb-1 capitalize text-[#e7ddd3] tracking-wide flex items-center gap-2',
  subtitle: 'text-sm text-[#8a8175] mb-6',
  input:
    'w-full bg-[#252525] border border-[#484540] rounded-lg px-4 py-2.5 text-sm text-[#e4e4e3] placeholder-[#6f675c] focus:outline-none focus:ring-2 focus:ring-[#b7a78b] focus:border-transparent transition',
  primaryBtn:
    'inline-flex items-center justify-center bg-[#b7a78b] hover:bg-[#d0c3a6] text-[#252525] text-sm font-semibold px-8 py-3 rounded-lg uppercase tracking-wide transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
  deleteBtn:
    'bg-red-600/90 hover:bg-red-600 text-white text-xs font-semibold uppercase px-4 py-2 rounded-md transition-colors',
  orderBtn:
    'bg-[#252525] border border-[#484540] hover:border-[#b7a78b] text-[#e4e4e3] text-sm leading-none px-3 py-2 rounded-md transition-colors disabled:opacity-30 disabled:hover:border-[#484540] disabled:cursor-not-allowed',
  tableWrap: 'overflow-x-auto rounded-lg border border-[#484540]',
  table: 'min-w-full divide-y divide-[#484540]',
  th: 'px-6 py-3 text-left text-xs font-semibold text-[#8a8175] uppercase tracking-wider bg-[#252525]',
  td: 'px-6 py-4 whitespace-nowrap text-sm text-[#cdc2ad]',
  tr: 'hover:bg-[#252525] transition-colors',
};

const ExhibitionForm = () => {
  const [exhibitionName, setExhibitionName] = useState('');
  const [exhibitionList, setExhibitionList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/exhibitions`);
      if (response.ok) {
        setExhibitionList(await response.json());
      } else {
        console.error('Error fetching data');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleExhibitionSubmit = async () => {
    if (!exhibitionName.trim()) return;
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/exhibitions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: exhibitionName }),
      });

      if (response.ok) {
        setExhibitionName('');
        alert('New Exhibition successfully created');
        fetchData();
      } else {
        console.error('Error creating Exhibition title:', response.statusText);
        alert('Failed to create the exhibition title.');
      }
    } catch (error) {
      console.error('Error creating Exhibition title:', error);
      alert('Failed to create the exhibition title.');
    } finally {
      setLoading(false);
    }
  };

  // The list is ordered by hand; swap a title with its neighbour.
  const handleMoveExhibition = async (exhibitionId, direction) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/exhibitions/${exhibitionId}/move`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ direction }),
      });

      if (response.ok) {
        fetchData();
      } else {
        const errorData = await response.json();
        console.error('Error moving exhibition:', errorData);
      }
    } catch (error) {
      console.error('Error moving exhibition:', error);
    }
  };

  const handleDeleteExhibition = async (exhibitionId) => {
    if (!window.confirm('Delete this exhibition title?')) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/exhibitions/${exhibitionId}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        setExhibitionList((prev) => prev.filter((exhibition) => exhibition._id !== exhibitionId));
      } else {
        console.error('Error deleting exhibition title:', response.statusText);
      }
    } catch (error) {
      console.error('Error deleting exhibition title:', error);
    }
  };

  return (
    <div className="flex min-h-screen bg-[#252525]" style={{ fontFamily: 'Muli' }}>
      <Sidebar />

      <div className="flex-1 bg-[#252525] min-h-screen pb-16">
        {/* Top bar */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[#484540] bg-[#1d1d1d] px-6 py-4">
          <p className="text-sm font-medium text-[#8a8175]">Admin Dashboard</p>
          <span className="text-sm font-semibold text-[#e7ddd3]">Welcome, Admin</span>
        </div>

        <div className="mx-auto max-w-5xl px-4 md:px-8 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight text-[#e7ddd3]">Exhibition Titles</h1>
            <p className="mt-1 text-sm text-[#8a8175]">Create and manage the exhibitions used across the site.</p>
          </div>

          {/* Create */}
          <div className={`${ui.card} mb-8`}>
            <h2 className={ui.title}>
              <LayoutGrid className="h-5 w-5 text-[#b7a78b]" /> New exhibition title
            </h2>
            <p className={ui.subtitle}>New titles are added to the top of the list.</p>

            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                className={ui.input}
                type="text"
                placeholder="e.g. Lagos Biennial"
                value={exhibitionName}
                onChange={(e) => setExhibitionName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleExhibitionSubmit()}
              />
              <button
                className={ui.primaryBtn}
                onClick={handleExhibitionSubmit}
                disabled={loading || !exhibitionName.trim()}
              >
                {loading ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>

          {/* List */}
          <div className={ui.card}>
            <h2 className={ui.title}>
              List of exhibitions
              <span className="ml-1 text-sm font-normal text-[#8a8175]">({exhibitionList.length})</span>
            </h2>
            <p className={ui.subtitle}>
              This order is what shows in the navbar dropdown — use the arrows to rearrange it.
            </p>

            <div className={ui.tableWrap}>
              <table className={ui.table}>
                <thead>
                  <tr>
                    <th className={ui.th}>s/n</th>
                    <th className={ui.th}>exhibition</th>
                    <th className={ui.th}>order</th>
                    <th className={ui.th}>delete</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#484540]">
                  {exhibitionList.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-6 py-10 text-center text-sm text-[#6f675c]">
                        No exhibitions created yet.
                      </td>
                    </tr>
                  ) : (
                    exhibitionList.map((exhibition, index) => (
                      <tr className={ui.tr} key={exhibition._id}>
                        <td className={ui.td}>{index + 1}</td>
                        <td className={`${ui.td} capitalize text-[#e7ddd3]`}>{exhibition.name}</td>
                        <td className={ui.td}>
                          <div className="flex items-center gap-2">
                            <button
                              className={ui.orderBtn}
                              title="Move up"
                              disabled={index === 0}
                              onClick={() => handleMoveExhibition(exhibition._id, 'up')}
                            >
                              ↑
                            </button>
                            <button
                              className={ui.orderBtn}
                              title="Move down"
                              disabled={index === exhibitionList.length - 1}
                              onClick={() => handleMoveExhibition(exhibition._id, 'down')}
                            >
                              ↓
                            </button>
                          </div>
                        </td>
                        <td className={ui.td}>
                          <button
                            className={ui.deleteBtn}
                            onClick={() => handleDeleteExhibition(exhibition._id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExhibitionForm;
